<template>
  <div class="model-card" ref="modelCard">
    <!-- Elegant Loading Overlay -->
    <transition name="fade">
      <div v-if="loading" class="loading-overlay">
        <div class="loader-container">
          <div class="loader-icon-wrapper">
            <v-icon class="loader-icon" size="48" color="primary">mdi-cube-scan</v-icon>
            <div class="pulse-ring"></div>
            <div class="pulse-ring pulse-ring-delay"></div>
          </div>
          
          <div class="loader-content">
            <h3 class="loader-title">Loading 3D Model</h3>
            <div class="progress-bar-container">
              <div class="progress-bar" :style="{ width: loadingProgress + '%' }"></div>
            </div>
            <p class="loader-percentage">{{ loadingProgress }}%</p>
          </div>
        </div>
      </div>
    </transition>

    <!-- Error Message -->
    <transition name="fade">
      <div v-if="error" class="error-overlay">
        <div class="error-content">
          <div class="error-icon-wrapper">
            <v-icon size="56" color="error">mdi-alert-circle-outline</v-icon>
          </div>
          <h3>Unable to Load Model</h3>
          <p>{{ error }}</p>
          <v-btn 
            variant="flat"
            color="primary" 
            @click="retryLoad"
            prepend-icon="mdi-refresh"
          >
            Try Again
          </v-btn>
        </div>
      </div>
    </transition>

    <!-- Babylon.js container -->
    <div ref="sceneContainer" class="babylon-container" :class="{ loaded: !loading && !error }"></div>

    <!-- Controls Info -->
    <transition name="slide-up">
      <div v-if="!loading && !error && showControls" class="controls-info">
        <v-icon size="18" class="controls-icon">mdi-gesture-tap</v-icon>
        <span class="controls-text">Drag to rotate • Scroll to zoom</span>
        <v-btn 
          icon 
          size="x-small" 
          variant="text" 
          @click="showControls = false"
          class="controls-close"
        >
          <v-icon size="14">mdi-close</v-icon>
        </v-btn>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue';
import {
  Engine,
  Scene,
  ArcRotateCamera,
  Vector3,
  HemisphericLight,
  SceneLoader,
  Color3,
  StandardMaterial,
  Animation,
} from '@babylonjs/core';
import '@babylonjs/loaders/glTF';

const modelCard = ref(null);
const loading = ref(true);
const loadingProgress = ref(0);
const error = ref(null);
const sceneContainer = ref(null);
const showControls = ref(true);

let engine = null;
let scene = null;
let canvas = null;
let camera = null;
let isInitialized = false;
let resizeObserver = null;
let loadingInterval = null;

// Marker and text names from Blender
const markerNames = [
  "2.02_Auditorium_Marker",
  "2.06_FH Fixed_Desks_Marker",
  "2.10_Meeting_Room_Marker",
  "2.42_FH_Team_Marker",
  "Community_Area_Marker",
  "Pinestack_Team_Marker",
  "2.05_acitoflux_Marker"
];

const textNames = [
  "2.02_Auditorium_Text",
  "2.06_FH_Fixed_Desks_Text",
  "2.10_Meeting_Room_Text",
  "2.42_FH_Team_Text",
  "Community_Area_Text",
  "Pinestack_Team_Text",
  "2.05_acitoflux_Text"
];

function onResize() {
  if (engine && !loading.value && isInitialized) {
    try {
      engine.resize();
    } catch (err) {
      console.warn('Error resizing engine:', err);
    }
  }
}

// Simulate smooth progress
function simulateProgress() {
  loadingProgress.value = 0;
  let progress = 0;
  
  loadingInterval = setInterval(() => {
    if (progress < 30) {
      progress += Math.random() * 5;
    } else if (progress < 60) {
      progress += Math.random() * 3;
    } else if (progress < 90) {
      progress += Math.random() * 2;
    }
    
    loadingProgress.value = Math.min(Math.floor(progress), 90);
    
    if (progress >= 90) {
      clearInterval(loadingInterval);
    }
  }, 200);
}

// Wait for container to have dimensions
async function waitForContainer() {
  return new Promise((resolve) => {
    const checkDimensions = () => {
      if (sceneContainer.value) {
        const rect = sceneContainer.value.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          console.log('Container ready:', rect.width, 'x', rect.height);
          resolve(true);
        } else {
          setTimeout(checkDimensions, 100);
        }
      } else {
        setTimeout(checkDimensions, 100);
      }
    };
    checkDimensions();
  });
}

async function initBabylon() {
  if (isInitialized) {
    console.warn('Already initialized');
    return;
  }

  try {
    loading.value = true;
    error.value = null;
    simulateProgress();

    // Wait for container to be ready
    await waitForContainer();

    if (!sceneContainer.value) {
      throw new Error('Scene container not found');
    }

    // Create canvas
    canvas = document.createElement('canvas');
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.display = 'block';
    canvas.setAttribute('touch-action', 'none');
    
    sceneContainer.value.innerHTML = '';
    sceneContainer.value.appendChild(canvas);

    // Small delay to ensure canvas is in DOM
    await nextTick();
    await new Promise(resolve => setTimeout(resolve, 100));

    console.log('Initializing Babylon engine...');

    // Initialize engine
    engine = new Engine(canvas, true, { 
      preserveDrawingBuffer: true, 
      stencil: true,
      antialias: true,
      adaptToDeviceRatio: true
    });

    if (!engine) {
      throw new Error('Failed to create Babylon engine');
    }

    // Create scene
    scene = new Scene(engine);
    scene.clearColor = new Color3(0.95, 0.96, 0.98);

    // Setup camera
    camera = new ArcRotateCamera('camera', Math.PI / 2, Math.PI / 3, 30, Vector3.Zero(), scene);
    camera.attachControl(canvas, true);
    camera.lowerRadiusLimit = 5;
    camera.upperRadiusLimit = 100;
    camera.wheelPrecision = 50;
    camera.pinchPrecision = 50;
    camera.panningSensibility = 50;

    // Add lighting
    const light = new HemisphericLight('light', new Vector3(0, 1, 0), scene);
    light.intensity = 1.2;

    // Create environment
    scene.createDefaultEnvironment({
      createSkybox: false,
      createGround: true,
      groundColor: new Color3(0.9, 0.92, 0.95),
      enableGroundShadow: true,
      groundYBias: 1,
    });

    // Image processing
    const cfg = scene.imageProcessingConfiguration;
    cfg.toneMappingEnabled = true;
    cfg.exposure = 0.5;
    cfg.contrast = 2.2;
    cfg.saturation = 3.8;

    console.log('Loading model from /assets/HBC.glb...');

    // Load model
    await SceneLoader.ImportMeshAsync(
      null, 
      '/assets/', 
      'HBC_EDIT.glb', 
      scene,
      (event) => {
        if (event.lengthComputable && loadingProgress.value < 90) {
          const modelProgress = Math.floor((event.loaded / event.total) * 40) + 50;
          loadingProgress.value = Math.min(modelProgress, 90);
        }
      }
    ).then(({ meshes }) => {
      console.log('Model loaded successfully, meshes:', meshes.length);
      loadingProgress.value = 92;

      // Apply materials to markers
      markerNames.forEach(name => {
        const marker = meshes.find(m => m.name === name);
        if (marker) {
          const mat = new StandardMaterial("markerMat_" + name, scene);
          mat.emissiveColor = new Color3(0.23, 0.51, 0.96);
          mat.diffuseColor = new Color3(0.15, 0.4, 0.8);
          marker.material = mat;

          const baseScale = marker.scaling.clone();
          const pulse = new Animation("pulse_" + name, "scaling", 30, Animation.ANIMATIONTYPE_VECTOR3);
          pulse.setKeys([
            { frame: 0, value: baseScale },
            { frame: 15, value: baseScale.multiplyByFloats(1.15, 1.15, 1.15) },
            { frame: 30, value: baseScale }
          ]);
          marker.animations = [pulse];
          scene.beginAnimation(marker, 0, 30, true);
        }
      });

      loadingProgress.value = 96;

      // Apply materials to text
      textNames.forEach(name => {
        const textMesh = meshes.find(m => m.name === name);
        if (textMesh) {
          const textMat = new StandardMaterial("textMat_" + name, scene);
          textMat.emissiveColor = new Color3(0.23, 0.51, 0.96);
          textMat.diffuseColor = new Color3(0.3, 0.6, 1);
          textMat.specularColor = new Color3(0.5, 0.7, 1);
          textMat.alpha = 1;
          textMat.backFaceCulling = false;
          textMesh.material = textMat;

          textMesh.position.y += 0.01;
          textMesh.receiveShadows = false;
          textMesh.isPickable = true;
        }
      });

      loadingProgress.value = 100;
    });

    // Start render loop
    engine.runRenderLoop(() => {
      if (scene && isInitialized) {
        scene.render();
      }
    });

    // Mark as initialized
    isInitialized = true;

    // Small delay before hiding loader
    setTimeout(() => {
      loading.value = false;
      if (loadingInterval) clearInterval(loadingInterval);
      console.log('Model fully loaded and ready');
    }, 500);

    // Add window resize listener
    window.addEventListener('resize', onResize);

    // Add ResizeObserver for container changes
    if (modelCard.value) {
      resizeObserver = new ResizeObserver(() => {
        onResize();
      });
      resizeObserver.observe(modelCard.value);
    }

  } catch (err) {
    console.error('Error initializing Babylon scene:', err);
    error.value = err.message || 'Failed to load 3D model. Please check if HBC.glb exists in /public/assets/';
    loading.value = false;
    isInitialized = false;
    if (loadingInterval) clearInterval(loadingInterval);
  }
}

function disposeScene() {
  try {
    isInitialized = false;

    if (loadingInterval) {
      clearInterval(loadingInterval);
      loadingInterval = null;
    }

    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    }

    if (scene) {
      scene.dispose();
      scene = null;
    }
    
    if (engine) {
      engine.stopRenderLoop();
      engine.dispose();
      engine = null;
    }
    
    if (canvas && canvas.parentNode) {
      canvas.parentNode.removeChild(canvas);
      canvas = null;
    }
    
    window.removeEventListener('resize', onResize);
    
    console.log('Scene disposed');
  } catch (err) {
    console.warn('Error disposing scene:', err);
  }
}

function retryLoad() {
  error.value = null;
  loading.value = true;
  loadingProgress.value = 0;
  disposeScene();
  
  setTimeout(() => {
    initBabylon();
  }, 500);
}

// Initialize on mount
onMounted(() => {
  console.log('Component mounted');
  
  // Wait for parent to be fully rendered
  nextTick(() => {
    setTimeout(() => {
      initBabylon();
    }, 500);
  });
});

onBeforeUnmount(() => {
  console.log('Component unmounting');
  disposeScene();
});

defineExpose({ retryLoad });
</script>

<style scoped>
.model-card {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
}

.babylon-container {
  width: 100%;
  height: 100%;
  flex: 1;
  position: relative;
  opacity: 0;
  transition: opacity 0.8s ease;
  min-height: 0;
}

.babylon-container.loaded {
  opacity: 1;
}

/* ==================== Loading Overlay ==================== */
.loading-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, 
    rgba(255, 255, 255, 0.98) 0%, 
    rgba(248, 250, 252, 0.95) 50%,
    rgba(239, 246, 255, 0.98) 100%
  );
  backdrop-filter: blur(20px);
  z-index: 1000;
  border-radius: 12px;
}

.loader-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  padding: 2rem;
}

.loader-icon-wrapper {
  position: relative;
  width: 100px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loader-icon {
  animation: rotate3d 3s linear infinite;
  filter: drop-shadow(0 4px 12px rgba(59, 130, 246, 0.3));
  z-index: 2;
}

@keyframes rotate3d {
  0% { transform: rotateY(0deg) rotateX(0deg); }
  50% { transform: rotateY(180deg) rotateX(10deg); }
  100% { transform: rotateY(360deg) rotateX(0deg); }
}

.pulse-ring {
  position: absolute;
  inset: 0;
  border: 3px solid rgba(59, 130, 246, 0.4);
  border-radius: 50%;
  animation: pulseRing 2s ease-out infinite;
}

.pulse-ring-delay {
  animation-delay: 1s;
}

@keyframes pulseRing {
  0% {
    transform: scale(0.8);
    opacity: 1;
  }
  100% {
    transform: scale(1.5);
    opacity: 0;
  }
}

.loader-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  max-width: 350px;
}

.loader-title {
  color: #1e293b;
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.5px;
}

.progress-bar-container {
  width: 250px;
  height: 6px;
  background: rgba(59, 130, 246, 0.1);
  border-radius: 10px;
  overflow: hidden;
  position: relative;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6 0%, #2563eb 100%);
  border-radius: 10px;
  transition: width 0.3s ease;
  position: relative;
  overflow: hidden;
}

.progress-bar::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.6),
    transparent
  );
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

.loader-percentage {
  color: #3b82f6;
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0;
  font-variant-numeric: tabular-nums;
  text-shadow: 0 2px 8px rgba(59, 130, 246, 0.2);
}

/* ==================== Error Overlay ==================== */
.error-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 100%);
  z-index: 1000;
  border-radius: 12px;
}

.error-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  padding: 2rem;
  text-align: center;
  max-width: 400px;
}

.error-icon-wrapper {
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(239, 68, 68, 0.1);
  border-radius: 50%;
}

.error-content h3 {
  color: #1e293b;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
}

.error-content p {
  color: #64748b;
  font-size: 0.95rem;
  margin: 0;
  line-height: 1.6;
}

/* ==================== Controls Info ==================== */
.controls-info {
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 8px 16px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 100%);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 50px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(16px);
  z-index: 100;
  max-width: calc(100% - 2rem);
}

.controls-icon {
  color: #3b82f6;
  flex-shrink: 0;
}

.controls-text {
  font-size: 0.8125rem;
  color: #475569;
  font-weight: 500;
  white-space: nowrap;
}

.controls-close {
  color: #94a3b8;
  transition: color 0.2s ease;
  flex-shrink: 0;
}

.controls-close:hover {
  color: #64748b;
}

/* ==================== Transitions ==================== */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from {
  opacity: 0;
  transform: translateX(-50%) translateY(20px);
}

.slide-up-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(10px);
}

/* ==================== Responsive - Tablet ==================== */
@media (max-width: 1024px) {
  .loader-container {
    gap: 1.25rem;
  }

  .loader-icon-wrapper {
    width: 90px;
    height: 90px;
  }

  .loader-title {
    font-size: 1.125rem;
  }

  .progress-bar-container {
    width: 220px;
  }

  .loader-percentage {
    font-size: 1.5rem;
  }

  .controls-info {
    padding: 7px 14px;
  }

  .controls-text {
    font-size: 0.75rem;
  }
}

/* ==================== Responsive - Mobile ==================== */
@media (max-width: 768px) {
  .loader-container {
    padding: 1.5rem;
    gap: 1rem;
  }

  .loader-icon-wrapper {
    width: 70px;
    height: 70px;
  }

  .loader-title {
    font-size: 1rem;
  }

  .progress-bar-container {
    width: 180px;
    height: 5px;
  }

  .loader-percentage {
    font-size: 1.375rem;
  }

  .controls-info {
    bottom: 0.75rem;
    padding: 6px 12px;
    gap: 0.375rem;
  }

  .controls-text {
    font-size: 0.6875rem;
  }

  .error-content {
    padding: 1.5rem;
  }

  .error-icon-wrapper {
    width: 64px;
    height: 64px;
  }

  .error-content h3 {
    font-size: 1.25rem;
  }

  .error-content p {
    font-size: 0.875rem;
  }
}

/* ==================== Responsive - Small Mobile ==================== */
@media (max-width: 480px) {
  .loader-container {
    padding: 1rem;
  }

  .loader-icon-wrapper {
    width: 60px;
    height: 60px;
  }

  .loader-title {
    font-size: 0.9375rem;
  }

  .progress-bar-container {
    width: 150px;
    height: 4px;
  }

  .loader-percentage {
    font-size: 1.25rem;
  }

  .controls-info {
    bottom: 0.5rem;
    padding: 5px 10px;
    gap: 0.25rem;
    font-size: 0.625rem;
  }

  .controls-text {
    font-size: 0.625rem;
  }

  .error-content {
    padding: 1rem;
  }

  .error-icon-wrapper {
    width: 56px;
    height: 56px;
  }

  .error-content h3 {
    font-size: 1.125rem;
  }
}
</style>